import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  makeStyles,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  textField: {
    marginTop: theme.spacing(2),
  },
}));

const AiModelModal = ({ open, onClose, aiModelId, reloadModels }) => {
  const classes = useStyles();
    const [model, setModel] = useState({
      name: "",
      apiIdentifier: "",
      apiIdentifierI2I: "",
      apiParameters: "{}",
      type: "Text to Image", // Categoría/Tipo
      costPrice: 0,
      officialPrice: 0,
      costPricePrompt: 0,
      salePricePrompt: 0,
      costPriceCompletion: 0,
      salePriceCompletion: 0,
      credits: 0,
      active: true,
      uiResolution: "",
      uiQuality: "",
      uiEnablePro: false,
    });

  useEffect(() => {
    if (aiModelId && open) {
      const fetchModel = async () => {
        try {
          const { data } = await api.get(`/ai-models`);
          const found = data.find((m) => m.id === aiModelId);
          if (found) {
            const parsedParams = found.apiParameters || {};
            setModel({
              name: found.name,
              apiIdentifier: found.apiIdentifier,
              apiIdentifierI2I: found.apiIdentifierI2I || "",
              apiParameters: found.apiParameters ? JSON.stringify(found.apiParameters) : "{}",
              type: found.type,
              costPrice: found.costPrice,
              officialPrice: found.officialPrice || 0,
              costPricePrompt: found.costPricePrompt || 0,
              salePricePrompt: found.salePricePrompt || 0,
              costPriceCompletion: found.costPriceCompletion || 0,
              salePriceCompletion: found.salePriceCompletion || 0,
              credits: found.credits,
              active: found.active,
              uiResolution: parsedParams.resolution || "",
              uiQuality: parsedParams.quality || "",
              uiEnablePro: parsedParams.enable_pro || false,
            });
          }
        } catch (err) {
          toastError(err);
        }
      };
      fetchModel();
    } else {
      setModel({
        name: "",
        apiIdentifier: "",
        apiIdentifierI2I: "",
        apiParameters: "{}",
        type: "GPT Image",
        costPrice: 0,
        officialPrice: 0,
        costPricePrompt: 0,
        salePricePrompt: 0,
        costPriceCompletion: 0,
        salePriceCompletion: 0,
        credits: 0,
        active: true,
        uiResolution: "",
        uiQuality: "",
      });
    }
  }, [aiModelId, open]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setModel((prev) => ({
      ...prev,
      [name]: name === "active" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...model };
      try {
        let paramsObj = JSON.parse(model.apiParameters);
        if (model.uiResolution) paramsObj.resolution = model.uiResolution;
        else delete paramsObj.resolution;
        
        if (model.uiQuality) paramsObj.quality = model.uiQuality;
        else delete paramsObj.quality;
        
        if (model.uiEnablePro) paramsObj.enable_pro = true;
        else delete paramsObj.enable_pro;
        
        payload.apiParameters = paramsObj;
      } catch (e) {
        toastError(new Error(i18n.t("aiModels.invalidJson")));
        return;
      }

      if (aiModelId) {
        await api.put(`/ai-models/${aiModelId}`, payload);
        toast.success(i18n.t("aiModels.modelUpdated"));
      } else {
        await api.post("/ai-models", payload);
        toast.success(i18n.t("aiModels.modelAdded"));
      }
      reloadModels();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {aiModelId ? i18n.t("aiModels.editModel") : i18n.t("aiModels.addModelTitle")}
      </DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          <TextField
            label={i18n.t("aiModels.commercialName")}
            name="name"
            value={model.name}
            onChange={handleChange}
            fullWidth
            className={classes.textField}
            required
          />
          <TextField
            label={i18n.t("aiModels.apiId")}
            name="apiIdentifier"
            value={model.apiIdentifier}
            onChange={handleChange}
            fullWidth
            className={classes.textField}
            required
          />
          <FormControl fullWidth className={classes.textField}>
            <InputLabel>{i18n.t("aiModels.category")}</InputLabel>
            <Select name="type" value={model.type} onChange={handleChange}>
              <MenuItem value="Text to Image">Text to Image</MenuItem>
              <MenuItem value="Image to Image">Image to Image</MenuItem>
              <MenuItem value="Text to Video">Text to Video</MenuItem>
              <MenuItem value="Image to Video">Image to Video</MenuItem>
              <MenuItem value="Video to Video">Video to Video</MenuItem>
              <MenuItem value="Text Generation">Text Generation</MenuItem>
              <MenuItem value="Audio Transcription">Audio Transcription</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth className={classes.textField}>
            <InputLabel>{i18n.t("aiModels.resolutionOpt")}</InputLabel>
            <Select name="uiResolution" value={model.uiResolution} onChange={handleChange}>
              <MenuItem value=""><em>{i18n.t("aiModels.default")}</em></MenuItem>
              <MenuItem value="1K">1K</MenuItem>
              <MenuItem value="2K">2K</MenuItem>
              <MenuItem value="4K">4K</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth className={classes.textField}>
            <InputLabel>{i18n.t("aiModels.qualityOpt")}</InputLabel>
            <Select name="uiQuality" value={model.uiQuality} onChange={handleChange}>
              <MenuItem value=""><em>{i18n.t("aiModels.default")}</em></MenuItem>
              {(() => {
                const name = (model.name || '').toLowerCase();
                if (name.includes('gpt')) {
                  return [
                    <MenuItem key="medium" value="medium">medium</MenuItem>,
                    <MenuItem key="high" value="high">high</MenuItem>
                  ];
                }
                if (name.includes('seedream')) {
                  return [
                    <MenuItem key="basic" value="basic">basic</MenuItem>,
                    <MenuItem key="high" value="high">high</MenuItem>
                  ];
                }
                return [
                  <MenuItem key="basic" value="basic">basic</MenuItem>,
                  <MenuItem key="medium" value="medium">medium</MenuItem>,
                  <MenuItem key="high" value="high">high</MenuItem>
                ];
              })()}
            </Select>
          </FormControl>
          {(model.name || "").toLowerCase().includes("grok") && (
            <FormControl fullWidth className={classes.textField}>
              <FormControlLabel
                control={
                  <Switch
                    checked={model.uiEnablePro}
                    onChange={(e) => setModel({ ...model, uiEnablePro: e.target.checked })}
                    name="uiEnablePro"
                    color="primary"
                  />
                }
                label={i18n.t("aiModels.enablePro")}
              />
            </FormControl>
          )}
          <TextField
            label={i18n.t("aiModels.extraParams")}
            name="apiParameters"
            value={model.apiParameters}
            onChange={handleChange}
            fullWidth
            className={classes.textField}
            multiline
            minRows={2}
            helperText={i18n.t("aiModels.extraParamsHelp")}
          />
          <TextField
            label={i18n.t("aiModels.officialPrice")}
            name="officialPrice"
            type="number"
            value={model.officialPrice}
            onChange={handleChange}
            fullWidth
            className={classes.textField}
            inputProps={{ step: "0.0001" }}
            helperText={i18n.t("aiModels.officialPriceHelp")}
          />
          {model.type !== "Text Generation" && (
            <>
              <TextField
                label={i18n.t("aiModels.unitBaseCost")}
                name="costPrice"
                type="number"
                value={model.costPrice}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
              />
              <TextField
                label={i18n.t("aiModels.unitSalePrice")}
                name="credits"
                type="number"
                value={model.credits}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
                helperText={i18n.t("aiModels.salePriceHelp")}
              />
            </>
          )}

          {model.type === "Text Generation" && (
            <>
              <TextField
                label={i18n.t("aiModels.costPrompt")}
                name="costPricePrompt"
                type="number"
                value={model.costPricePrompt}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
              />
              <TextField
                label={i18n.t("aiModels.salePrompt")}
                name="salePricePrompt"
                type="number"
                value={model.salePricePrompt}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
              />
              <TextField
                label={i18n.t("aiModels.costCompletion")}
                name="costPriceCompletion"
                type="number"
                value={model.costPriceCompletion}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
              />
              <TextField
                label={i18n.t("aiModels.saleCompletion")}
                name="salePriceCompletion"
                type="number"
                value={model.salePriceCompletion}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                inputProps={{ step: "0.0001" }}
              />
            </>
          )}
          <TextField
            label={i18n.t("aiModels.creditsCost")}
            name="credits"
            type="number"
            value={model.credits}
            onChange={handleChange}
            fullWidth
            className={classes.textField}
            inputProps={{ step: "any" }}
            helperText={i18n.t("aiModels.creditsCostHelp")}
          />
          <FormControlLabel
            className={classes.textField}
            control={
              <Switch
                checked={model.active}
                onChange={handleChange}
                name="active"
                color="primary"
              />
            }
            label={i18n.t("aiModels.activeShow")}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {i18n.t("aiModels.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("aiModels.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiModelModal;
